import { useState, useEffect } from 'react';
import { SearchableSelect } from './SearchableSelect';
import { useGetPrograms } from '../api/useGetPrograms';
import { useGetProgramDetail } from '../api/useProgram';

interface Option {
  value: string;
  label: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
}

export const ProgramsDropdown = ({ value, onChange }: Props) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);

  useEffect(() => {
    if (!search) {
      setDebouncedSearch('');
      return;
    }
    const timer = setTimeout(() => { setDebouncedSearch(search); }, 1000);
    return () => { clearTimeout(timer); };
  }, [search]);

  const { data, isLoading } = useGetPrograms({ q: debouncedSearch || undefined });

  const { programs = [] } = data?.payload ?? {};

  const options = programs.map((program) => ({
    value: program.uid,
    label: program.name,
  }));

  const knownOptions =
    selectedOption && !options.find((o) => o.value === selectedOption.value)
      ? [selectedOption, ...options]
      : options;

  const needsLabel = value !== '' && !knownOptions.find((o) => o.value === value);
  const { data: detail } = useGetProgramDetail(needsLabel ? value : '');
  const detailProgram = needsLabel ? detail?.payload?.program : undefined;

  const mergedOptions = detailProgram
    ? [{ value: detailProgram.uid, label: detailProgram.name }, ...knownOptions]
    : knownOptions;

  const handleChange = (v: string) => {
    if (!v) { setSelectedOption(null); onChange(''); return; }
    const found = options.find((o) => o.value === v);
    if (found) { setSelectedOption(found); }
    onChange(v);
  };

  return (
    <SearchableSelect
      options={mergedOptions}
      value={value}
      onChange={handleChange}
      onSearchChange={setSearch}
      placeholder="Select a program"
      isLoading={isLoading}
    />
  );
};
